import { ActionIcon, Alert, Blockquote, Button, Group, Text } from "@mantine/core";
import { IComment, IEditorMetadata, IUserDepricated } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconPencil, IconSend, IconTrash, IconX } from "@tabler/icons-react";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "..";
import ErrorBoundary from "../../util/ErrorBoundary";
import Avatar from "../Avatar";
import RichTextEditor, {
  lexicalStateToPlainText,
  plainTextToLexicalState,
  RichTextReader,
} from "../RichTextEditorMantine/RichTextEditor";
import EmojiReactions from "./EmojiReactions";
import classes from "./SingleComment.module.css";

export const CiteComponent: React.FC<{ name: string; date: Date; actions?: React.ReactNode }> = ({
  name,
  date,
  actions,
}) => {
  const formatted = useMemo(() => moment(date).format("DD.MM.YYYY, HH:mm"), [date]);

  return (
    <Group justify="space-between" align="center" wrap="wrap" gap="xs" mt="xs">
      <Text size="sm" fs="italic" c="dimmed" style={{ opacity: 0.6 }}>
        <Text span fw={700}>
          {name}
        </Text>{" "}
        - {formatted}
      </Text>
      {actions && (
        <Group gap={0} align="center" wrap="wrap">
          {actions}
        </Group>
      )}
    </Group>
  );
};

const SingleComment: React.FC<{
  comment: IComment;
  editEnabled: boolean;
  deleteEnabled: boolean;
  members: IProjectMemberApiItem[];
  onSave: (text: string, metadata: IEditorMetadata) => void;
  onDelete: () => void;
  reactions?: { user: { _id: string; name: string }; date: string; emoji: string }[];
  onReact?: (emoji: string) => void;
  currentUserId?: string;
}> = ({ comment, editEnabled, deleteEnabled, onSave, onDelete, members, reactions, onReact, currentUserId }) => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const hasReactionControls = Boolean(onReact && currentUserId);
  const hasFooterControls = editEnabled || deleteEnabled || hasReactionControls;

  const createdById = typeof comment.createdBy === "string" ? comment.createdBy : comment.createdBy?._id;

  const createdByName = comment.createdByName ?? (comment.createdBy as IUserDepricated)?.name ?? "Ukjent";

  const createdDate = useMemo(() => {
    const raw = comment.createdDate || comment.createdAt;
    return raw ? new Date(raw) : new Date();
  }, [comment.createdDate, comment.createdAt]);

  const avatarIcon = <Avatar id={createdById} name={createdByName} />;
  const commentActions = hasFooterControls ? (
    <>
      {hasReactionControls && (
        <EmojiReactions reactions={reactions ?? []} currentUserId={currentUserId!} onReact={onReact!} />
      )}

      {deleteEnabled && (
        <ConfirmDialog
          confirmCb={() => onDelete()}
          title="Slett kommentar"
          message="Er du sikker på at du vil slette denne kommentar?"
        >
          <ActionIcon variant="subtle" color="gray" size="sm" className={classes.actionButton}>
            <IconTrash size={16} />
          </ActionIcon>
        </ConfirmDialog>
      )}

      {editEnabled && !editMode && (
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          className={classes.actionButton}
          onClick={() => setEditMode(true)}
        >
          <IconPencil size={16} />
        </ActionIcon>
      )}
    </>
  ) : null;

  return (
    <ErrorBoundary customComponent={<Alert color="danger">En feil skjedde! Melding kan ikke vises.</Alert>}>
      <Blockquote
        className={classes.comment}
        icon={avatarIcon}
        mt="xs"
        styles={{
          root: {
            paddingTop: "1rem",
            paddingBottom: "0.5rem",
            paddingRight: "0.5rem",
            position: "relative",
          },
        }}
      >
        {editMode === false && <RichTextReader richTextTree={comment.text} />}

        <EditComment
          editMode={editMode}
          setEditMode={setEditMode}
          text={comment.text}
          onSave={onSave}
          members={members}
        />

        <CiteComponent name={createdByName} date={createdDate} actions={commentActions} />
      </Blockquote>
    </ErrorBoundary>
  );
};

export default SingleComment;

const EditComment: React.FC<{
  editMode: boolean;
  setEditMode: (bool: boolean) => void;
  text: string;
  onSave: (text: string, metadata: IEditorMetadata) => void;
  members: IProjectMemberApiItem[];
}> = ({ editMode, setEditMode, text, onSave, members }) => {
  const [editedText, setEditedText] = useState<any>(null);
  const [editorMetadata, setEditorMetadata] = useState<IEditorMetadata>(null);
  const [hasImages, setHasImages] = useState(false);

  const abortUpdate = () => {
    setEditedText(null);
    setEditMode(false);
    setEditorMetadata(null);
  };

  const save = async () => {
    const plainText = await lexicalStateToPlainText(editedText);
    onSave(JSON.stringify(editedText), { ...editorMetadata, plainText });
    setEditMode(false);
    setEditorMetadata(null);
  };

  useEffect(() => {
    const formatText = async () => {
      const result = await plainTextToLexicalState(text);
      setEditedText(result);
    };

    if (editedText === null) {
      formatText();
    }

    const t = setTimeout(() => {
      if (editedText === null) {
        formatText();
      }
    }, 100);

    return () => clearTimeout(t);
  }, [editedText, text]);

  const handleChange = (editorState: any, _plainText: string, nextHasImages: boolean) => {
    setEditedText(editorState);
    setHasImages(nextHasImages);
  };

  if (!editMode) return null;

  return (
    <div style={{ width: "100%" }}>
      <RichTextEditor
        richTextTree={editedText}
        handleMentions={(e) => setEditorMetadata({ ...editorMetadata, mentionIdArray: e })}
        onChange={handleChange}
        members={members}
      />

      <Group mt="xs" gap="xs" justify="flex-end">
        <Button
          disabled={(!editedText || editedText.length < 1) && hasImages === false}
          leftSection={<IconSend size={16} />}
          variant="filled"
          size="sm"
          onClick={async () => {
            await save();
          }}
        >
          Lagre
        </Button>

        <Button onClick={() => abortUpdate()} size="sm" variant="default">
          <IconX size={16} />
        </Button>
      </Group>
    </div>
  );
};
